import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText, Upload, Loader2, Search, Check, X, Clock, AlertTriangle,
  Eye, RefreshCw, Archive, Copy, Ban, ChevronRight, Filter, Inbox, Send,
  FlaskConical, ScanLine, Stethoscope, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  unreviewed: 'Needs Review', deferred: 'Deferred', filed: 'Filed',
  duplicate: 'Duplicate', unreadable: 'Unreadable', rejected: 'Rejected',
};

const CONFIDENCE_COLORS = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-red-50 text-red-600 border-red-200',
};

export default function DocumentInbox() {
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('unreviewed');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [categories, setCategories] = useState({});
  const [patientSearch, setPatientSearch] = useState('');
  const [coverSheet, setCoverSheet] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const fileRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await api.get('/documents/my-queue');
      setDocuments(res.data.documents || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Auto-open document from ?doc= param (coming from My Day)
  useEffect(() => {
    const docId = searchParams.get('doc');
    if (docId && documents.length > 0) {
      const doc = documents.find(d => d.id === docId);
      if (doc) { setSelectedDoc(doc); setDetailOpen(true); }
    }
  }, [searchParams, documents]);

  useEffect(() => {
    api.get('/patients').then(r => setPatients(r.data.patients || [])).catch(() => {});
    api.get('/documents/categories').then(r => setCategories(r.data.labels || {})).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${file.name} uploaded — processing`);
      // Poll for OCR completion
      const docId = res.data.document?.id;
      if (docId) {
        setTimeout(async () => {
          await fetchDocs();
        }, 5000);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const reviewAction = async (docId, action, patientId = null, category = null) => {
    try {
      const res = await api.post(`/documents/${docId}/review`, { action, patient_id: patientId, category });
      toast.success(`Document ${action.replace('_', ' ')}`);
      setDetailOpen(false);
      setSelectedDoc(null);
      fetchDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const openDetail = async (doc) => {
    setSelectedDoc(doc);
    setCoverSheet(null);
    setDetailOpen(true);
    // Fetch cover sheet
    try {
      const csRes = await api.get(`/documents/${doc.id}/cover-sheet`);
      setCoverSheet(csRes.data.cover_sheet);
    } catch {}
    // Fetch active encounters for send-to-encounter
    if (doc.patient_match_candidates?.[0]?.patient_id || doc.selected_patient_id) {
      const pid = doc.selected_patient_id || doc.patient_match_candidates[0].patient_id;
      try {
        const encRes = await api.get(`/encounters/patient/${pid}`);
        setEncounters((encRes.data.encounters || []).filter(e => e.status !== 'finalized').slice(0, 5));
      } catch {}
    }
  };

  const filteredPatients = patientSearch
    ? patients.filter(p => {
        const name = `${p.name?.first} ${p.name?.last}`.toLowerCase();
        return name.includes(patientSearch.toLowerCase());
      }).slice(0, 8)
    : [];

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="document-inbox">
      {/* Header */}
      <div className="px-6 py-3 border-b bg-white/80 backdrop-blur-xl flex items-center gap-3 shrink-0">
        <Inbox className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Document Inbox</h1>
        <div className="flex-1" />

        {/* Filter */}
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="doc-filter">
            <Filter className="w-3 h-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unreviewed">Needs Review</SelectItem>
            <SelectItem value="deferred">Deferred</SelectItem>
            <SelectItem value="filed">Filed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>

        {/* Upload */}
        <input type="file" ref={fileRef} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif"
          onChange={handleUpload} data-testid="doc-file-input" />
        <Button size="sm" className="h-8 text-xs gap-1" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="doc-upload-btn">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Uploading...' : 'Upload Document'}
        </Button>

        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={fetchDocs}>
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>

        <Badge variant="outline" className="text-xs">{documents.length} documents</Badge>
      </div>

      {/* Document List — amber/blue card design matching My Day */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-5xl mx-auto" data-testid="doc-queue-list">
          {documents.length === 0 ? (
            <div className="text-center py-16">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">No documents to review</p>
            </div>
          ) : (
            <>
              {/* Needs Attention */}
              {(() => {
                const CATEGORY_ICONS = { lab_result: FlaskConical, imaging_report: ScanLine, consult_note: Stethoscope };
                const needsAttention = documents.filter(d => d.review_status !== 'signed' && d.triage_status !== 'filed');
                const workflowPending = documents.filter(d => d.review_status === 'signed');
                return (
                  <>
                    {needsAttention.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Needs Your Attention ({needsAttention.length})
                        </p>
                        <div className="space-y-1.5">
                          {needsAttention.map((doc, idx) => {
                            const CatIcon = CATEGORY_ICONS[doc.suggested_category] || FileText;
                            return (
                              <button key={doc.id} onClick={() => openDetail(doc)}
                                className="w-full text-left rounded-lg border-l-4 border-l-amber-400 border bg-card px-4 py-3 flex items-center gap-3 hover:shadow-md transition-all"
                                data-testid={`doc-row-${idx}`}>
                                <div className="p-1.5 rounded-lg bg-muted"><CatIcon className="w-3.5 h-3.5 text-muted-foreground" /></div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-semibold text-slate-700 truncate block">{doc.title || doc.filename}</span>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-muted-foreground">{doc.patient_name || doc.patient_match_candidates?.[0]?.patient_name || 'Unmatched'}</span>
                                    <span className="text-[10px] text-muted-foreground">{doc.source_name || doc.source_type}</span>
                                    {doc.suggested_category && (
                                      <Badge variant="outline" className="text-[8px] h-3.5">{doc.suggested_category?.replace(/_/g, ' ')}</Badge>
                                    )}
                                  </div>
                                </div>
                                <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {workflowPending.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" /> Workflow Pending ({workflowPending.length})
                        </p>
                        <div className="space-y-1.5">
                          {workflowPending.map((doc, idx) => {
                            const CatIcon = CATEGORY_ICONS[doc.suggested_category] || FileText;
                            return (
                              <button key={doc.id} onClick={() => openDetail(doc)}
                                className="w-full text-left rounded-lg border-l-4 border-l-blue-400 border bg-card px-4 py-3 flex items-center gap-3 hover:shadow-md transition-all"
                                data-testid={`doc-row-pending-${idx}`}>
                                <div className="p-1.5 rounded-lg bg-muted"><CatIcon className="w-3.5 h-3.5 text-muted-foreground" /></div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-semibold text-slate-700 truncate block">{doc.title || doc.filename}</span>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-muted-foreground">{doc.patient_name}</span>
                                    {doc.sign_off?.physician_note && <span className="text-[9px] text-slate-400 italic">{doc.sign_off.physician_note}</span>}
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 shrink-0">Tasked</Badge>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Document Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="doc-detail-dialog">
          {selectedDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {selectedDoc.filename}
                  {selectedDoc.review_status === 'filed' && <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">Filed</Badge>}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 mt-2">
                {/* Left: OCR Text */}
                <div>
                  <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Extracted Text</h3>
                  <div className="border rounded-lg p-3 bg-muted/20 text-xs leading-relaxed max-h-[300px] overflow-auto whitespace-pre-wrap font-mono" data-testid="doc-ocr-text">
                    {selectedDoc.ocr_text || (
                      selectedDoc.ocr_status === 'processing' ? 'Processing...' :
                      selectedDoc.ocr_status === 'failed' ? 'OCR extraction failed. Review original document.' :
                      'No text extracted.'
                    )}
                  </div>

                  <div className="mt-3 space-y-1 text-[10px] text-muted-foreground">
                    <div>Pages: {selectedDoc.page_count} | Size: {Math.round(selectedDoc.file_size / 1024)}KB</div>
                    <div>Source: {selectedDoc.source_type} | Uploaded by: {selectedDoc.uploaded_by_name}</div>
                    <div>OCR: {selectedDoc.ocr_status}</div>
                  </div>

                  {/* Cover Sheet Summary */}
                  {coverSheet && coverSheet.status === 'generated' && (
                    <div className="mt-3 border rounded-lg p-3 bg-blue-50/30" data-testid="doc-cover-sheet">
                      <div className="flex items-center gap-1 mb-2">
                        <Eye className="w-3 h-3 text-blue-600" />
                        <h4 className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Cover Sheet Summary</h4>
                      </div>
                      <p className="text-[9px] text-blue-500 mb-2 italic">{coverSheet.label}</p>
                      {coverSheet.highlights?.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {coverSheet.highlights.map((h, i) => (
                            <div key={i} className="flex items-baseline gap-1 text-[10px]">
                              <Badge variant="outline" className="text-[8px] h-3.5 shrink-0">{h.type}</Badge>
                              <span className="font-medium">{h.label}:</span>
                              <span className="text-muted-foreground">{h.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {coverSheet.actions?.length > 0 && (
                        <div className="pt-1 border-t space-y-0.5">
                          {coverSheet.actions.map((a, i) => (
                            <div key={i} className="text-[10px] flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                              <span className="font-medium">{a.signal}:</span>
                              <span className="text-muted-foreground truncate">{a.context}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right: Classification + Matching + Actions */}
                <div className="space-y-3">
                  {/* Category */}
                  <div>
                    <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Category</h3>
                    <Select value={selectedDoc.selected_category || selectedDoc.suggested_category || 'miscellaneous'}
                      onValueChange={(v) => reviewAction(selectedDoc.id, 'change_category', null, v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categories).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedDoc.category_confidence && (
                      <Badge variant="outline" className={`text-[9px] mt-1 ${CONFIDENCE_COLORS[selectedDoc.category_confidence]}`}>
                        {selectedDoc.category_confidence} confidence
                      </Badge>
                    )}
                  </div>

                  {/* Patient Match */}
                  <div>
                    <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Patient Match</h3>
                    {selectedDoc.patient_match_candidates?.length > 0 ? (
                      <div className="space-y-1">
                        {selectedDoc.patient_match_candidates.slice(0, 3).map((c, i) => (
                          <div key={i} className={`flex items-center justify-between p-1.5 rounded-md border text-xs cursor-pointer hover:bg-accent transition-colors ${
                            selectedDoc.selected_patient_id === c.patient_id ? 'border-primary bg-primary/5' : ''
                          }`} onClick={() => reviewAction(selectedDoc.id, 'change_patient', c.patient_id)}
                            data-testid={`doc-patient-match-${i}`}>
                            <div>
                              <span className="font-medium">{c.patient_name}</span>
                              <span className="text-muted-foreground ml-1">{c.mrn}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className={`text-[8px] h-3.5 ${CONFIDENCE_COLORS[c.confidence]}`}>
                                {c.confidence}
                              </Badge>
                              <span className="text-[9px] text-muted-foreground">{c.reasons?.join(', ')}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> No patient match found
                      </div>
                    )}
                    {/* Manual patient search */}
                    <div className="mt-2">
                      <div className="flex items-center gap-1 border rounded-md px-2 py-1">
                        <Search className="w-3 h-3 text-muted-foreground" />
                        <input className="flex-1 text-xs bg-transparent outline-none" placeholder="Search patient..."
                          value={patientSearch} onChange={e => setPatientSearch(e.target.value)} />
                      </div>
                      {filteredPatients.length > 0 && (
                        <div className="border rounded-md mt-1 max-h-[120px] overflow-auto">
                          {filteredPatients.map(p => (
                            <button key={p.id} className="w-full flex items-center justify-between px-2 py-1 text-xs hover:bg-accent"
                              onClick={() => { reviewAction(selectedDoc.id, 'change_patient', p.id); setPatientSearch(''); }}>
                              <span>{p.name?.first} {p.name?.last}</span>
                              <span className="text-muted-foreground">{p.mrn}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Streamlined Physician Review Actions */}
                  {selectedDoc.review_status !== 'filed' && selectedDoc.review_status !== 'signed' && (
                    <DocReviewActions doc={selectedDoc} onDone={() => { setDetailOpen(false); fetchDocs(); }} />
                  )}

                  {/* Already signed — show sign-off details */}
                  {(selectedDoc.review_status === 'signed' || selectedDoc.sign_off) && (
                    <div className="pt-2 border-t">
                      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-semibold text-emerald-800">Signed Off</span>
                        </div>
                        <p className="text-[10px] text-emerald-700">
                          {selectedDoc.sign_off?.signed_by_name} — {selectedDoc.sign_off?.signed_at ? new Date(selectedDoc.sign_off.signed_at).toLocaleString() : ''}
                        </p>
                        {selectedDoc.sign_off?.physician_note && (
                          <p className="text-xs text-emerald-600 mt-1 italic">{selectedDoc.sign_off.physician_note}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Send to Encounter */}
                  {encounters.length > 0 && (
                    <div className="space-y-1 pt-2 border-t" data-testid="doc-send-to-encounter">
                      <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Send to Encounter</h3>
                      {encounters.map(enc => (
                        <Button key={enc.id} variant="outline" size="sm" className="w-full h-7 text-[10px] gap-1 justify-start"
                          onClick={async () => {
                            try {
                              await api.post(`/documents/${selectedDoc.id}/send-to-encounter`, { encounter_id: enc.id });
                              toast.success('Document attached to encounter');
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          }}>
                          <FileText className="w-3 h-3" />
                          {enc.date?.split('T')[0]} — {enc.provider_name || 'Visit'}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ═══════════════════════════════════════
   DOC REVIEW ACTIONS — Streamlined layout
   ═══════════════════════════════════════ */

function DocReviewActions({ doc, onDone }) {
  const [note, setNote] = useState('');
  const [taskInstructions, setTaskInstructions] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [recipients, setRecipients] = useState({ frequent: [], all_staff: [] });
  const [showDefer, setShowDefer] = useState(false);
  const [deferDate, setDeferDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load frequent recipients
  useEffect(() => {
    api.get('/staff/my-frequent-recipients').then(r => {
      const data = r.data;
      setRecipients(data);
      // Auto-select default
      const def = (data.frequent || []).find(f => f.is_default);
      if (def) setSelectedRecipient(def);
      else if (data.frequent?.length > 0) setSelectedRecipient(data.frequent[0]);
    }).catch(() => {});
  }, []);

  const handleSignOff = async (sendToNurse) => {
    if (sendToNurse && !taskInstructions.trim()) { toast.error('Please enter task instructions'); return; }
    if (!sendToNurse && !note.trim()) {
      // Auto-fill "Noted" when physician note is empty — one-click acknowledge
      note = 'Noted';
    }
    setSubmitting(true);
    try {
      await api.post(`/documents/${doc.id}/sign-off`, {
        physician_note: note,
        action_instructions: sendToNurse ? taskInstructions : '',
        send_to_nurse: sendToNurse,
        nurse_id: selectedRecipient?.id || '',
        nurse_name: selectedRecipient?.name || '',
      });
      toast.success(sendToNurse ? `Signed off — task sent to ${selectedRecipient?.name || 'nurse'}` : 'Signed off & filed');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDefer = async (type) => {
    setSubmitting(true);
    try {
      if (type === 'chart_flag') {
        await api.post(`/documents/${doc.id}/sign-off`, {
          physician_note: `[DEFERRED — CHART FLAG] ${note || 'Discuss at next visit'}`,
          action_instructions: '', send_to_nurse: false,
        });
        const patId = doc.patient_id || doc.patient_match_candidates?.[0]?.patient_id;
        if (patId) {
          await api.post(`/patients/${patId}/clinical-notes`, {
            note_type: 'log',
            subject: `Chart Flag: ${doc.title || 'Document'} — discuss at next visit`,
            body: `${note || 'Discuss at next visit'}\n\nRe: ${doc.title || doc.filename}`,
          });
        }
        toast.success('Deferred — flag placed in chart');
      } else if (type === 'remind') {
        if (!deferDate) { toast.error('Please select a date'); setSubmitting(false); return; }
        await api.post(`/documents/${doc.id}/review`, {
          action: 'defer', notes: `Return on ${deferDate}. ${note}`,
        });
        toast.success(`Deferred — returns ${deferDate}`);
      }
      onDone();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const recipientName = selectedRecipient?.name || 'Select...';

  return (
    <div className="space-y-3 pt-2 border-t" data-testid="doc-review-actions">
      {/* View Original */}
      <Button size="sm" variant="outline" className="w-full h-7 text-[10px] gap-1"
        onClick={async () => {
          try {
            const res = await api.get(`/documents/${doc.id}/file`, { responseType: 'blob' });
            if (res.data instanceof Blob && res.data.type === 'application/pdf') {
              const url = URL.createObjectURL(res.data);
              window.open(url, '_blank');
            } else {
              // Response is JSON (no file available)
              toast.info('Original file not available — showing extracted text');
            }
          } catch { toast.error('Failed to load file'); }
        }} data-testid="doc-view-original">
        <Eye className="w-3 h-3" /> View Original Document
      </Button>

      {/* Physician Note */}
      <div>
        <label className="text-[9px] text-muted-foreground uppercase font-medium">Physician Note</label>
        <textarea className="w-full mt-1 text-xs border rounded-lg px-2.5 py-2 min-h-[40px] resize-none"
          value={note} onChange={e => setNote(e.target.value)}
          placeholder="Optional — if left blank, document will be signed as 'Noted'" data-testid="doc-physician-note" />
      </div>

      {/* Sign Off & File — small, right under note */}
      <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 text-emerald-600 border-emerald-200"
        onClick={() => handleSignOff(false)} disabled={submitting} data-testid="doc-sign-file">
        {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Sign Off & File
      </Button>

      {/* Divider */}
      <div className="border-t border-slate-200" />

      {/* Task Instructions */}
      <div>
        <label className="text-[9px] text-muted-foreground uppercase font-medium">Task Instructions</label>
        <textarea className="w-full mt-1 text-xs border border-blue-200 rounded-lg px-2.5 py-2 min-h-[36px] resize-none"
          value={taskInstructions} onChange={e => setTaskInstructions(e.target.value)}
          placeholder="Please schedule follow-up in 3 weeks..." data-testid="doc-task-input" />
      </div>

      {/* Sign Off & Send to [Nurse Name ▾] */}
      <div className="flex gap-1.5">
        <Button size="sm" className="flex-1 h-8 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700"
          onClick={() => handleSignOff(true)} disabled={submitting} data-testid="doc-sign-send-nurse">
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Sign Off & Send to {recipientName}
        </Button>
        {/* Nurse picker dropdown */}
        <div className="relative">
          <Button size="sm" variant="outline" className="h-8 px-2 border-blue-200"
            onClick={() => setShowRecipientPicker(!showRecipientPicker)} data-testid="doc-nurse-picker-btn">
            <ChevronRight className={`w-3 h-3 text-blue-500 transition-transform ${showRecipientPicker ? 'rotate-90' : ''}`} />
          </Button>
          {showRecipientPicker && (
            <div className="absolute right-0 bottom-full mb-1 w-56 bg-white rounded-xl border shadow-xl z-30 py-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-100"
              data-testid="recipient-picker">
              <p className="px-3 py-1 text-[9px] text-slate-400 uppercase font-semibold">Quick Pick</p>
              {recipients.frequent.map(r => (
                <button key={r.id} onClick={() => { setSelectedRecipient(r); setShowRecipientPicker(false); }}
                  className={`w-full text-left px-3 py-2 text-[10px] hover:bg-blue-50 flex items-center gap-2 ${selectedRecipient?.id === r.id ? 'bg-blue-50' : ''}`}>
                  {r.is_default && <span className="text-amber-500">★</span>}
                  <span className="font-medium text-slate-700">{r.name}</span>
                  <span className="text-[8px] text-slate-400 ml-auto">{r.role} · {r.send_count}x</span>
                  {selectedRecipient?.id === r.id && <Check className="w-3 h-3 text-blue-500" />}
                </button>
              ))}
              {recipients.frequent.length > 0 && recipients.all_staff.length > recipients.frequent.length && (
                <>
                  <div className="border-t my-1" />
                  <p className="px-3 py-1 text-[9px] text-slate-400 uppercase font-semibold">All Staff</p>
                  {recipients.all_staff.filter(s => !recipients.frequent.find(f => f.id === s.id)).map(s => (
                    <button key={s.id} onClick={() => { setSelectedRecipient(s); setShowRecipientPicker(false); }}
                      className="w-full text-left px-3 py-2 text-[10px] hover:bg-blue-50 flex items-center gap-2">
                      <span className="text-slate-700">{s.name}</span>
                      <span className="text-[8px] text-slate-400 ml-auto">{s.role}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom icon row — Defer + Reject (collapsed) */}
      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        <button onClick={() => setShowDefer(!showDefer)}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium transition-colors ${showDefer ? 'bg-amber-100 text-amber-700' : 'text-slate-400 hover:text-slate-600'}`}
          data-testid="doc-defer-toggle">
          <Clock className="w-3 h-3" /> Defer
        </button>
        <button onClick={() => {
          api.post(`/documents/${doc.id}/review`, { action: 'reject', notes: note }).then(() => { toast.success('Rejected'); onDone(); });
        }} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium text-slate-400 hover:text-red-600">
          <Ban className="w-3 h-3" /> Reject
        </button>
      </div>

      {/* Defer expanded options */}
      {showDefer && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-2.5 space-y-1.5 animate-in fade-in-0 slide-in-from-top-1 duration-100">
          <Button size="sm" variant="outline" className="w-full h-6 text-[9px] gap-1 border-amber-200 text-amber-700"
            onClick={() => handleDefer('chart_flag')} disabled={submitting}>
            <AlertTriangle className="w-2.5 h-2.5" /> Flag in Patient Chart
          </Button>
          <div className="flex gap-1">
            <input type="date" className="flex-1 text-[10px] border border-amber-200 rounded px-2 py-0.5 bg-white"
              value={deferDate} onChange={e => setDeferDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
            <Button size="sm" variant="outline" className="h-6 text-[9px] gap-0.5 border-amber-200 text-amber-700 shrink-0"
              onClick={() => handleDefer('remind')} disabled={submitting}>
              <Clock className="w-2.5 h-2.5" /> Remind
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

